/**
 * CricCam Pro — MVP Player Ranking Points Algorithm
 * Standardized formula for computing player performance points and MVP rank tiers.
 */

/**
 * Safely convert a value to a finite number, treating missing/invalid as 0.
 */
function toNumber(val) {
  const n = Number(val);
  return Number.isFinite(n) ? n : 0;
}

/**
 * Calculate MVP Points for a player's stats
 */
export function calculatePlayerMvpPoints(stats = {}) {
  const runs = toNumber(stats.runs);
  const fours = toNumber(stats.fours);
  const sixes = toNumber(stats.sixes);
  const wickets = toNumber(stats.wickets);
  const maidens = toNumber(stats.maidens);
  const catches = toNumber(stats.catches);
  const runOuts = toNumber(stats.runOuts);

  const points = (runs * 1) +
                 (fours * 1) +
                 (sixes * 2) +
                 (wickets * 25) +
                 (maidens * 8) +
                 (catches * 10) +
                 (runOuts * 10);

  return Math.round(points);
}

/**
 * Get MVP Rank Tier Badge Info
 */
export function getMvpRankTier(points = 0) {
  const safePts = toNumber(points);
  if (safePts >= 250) {
    return { title: '👑 MVP Legend', color: '#fbbf24', badge: 'LEGEND' };
  }
  if (safePts >= 150) {
    return { title: '🔥 Match Winner', color: '#f97316', badge: 'WINNER' };
  }
  if (safePts >= 75) {
    return { title: '⭐ Impact Player', color: '#60a5fa', badge: 'IMPACT' };
  }
  return { title: '🏏 Contender', color: '#94a3b8', badge: 'CONTENDER' };
}

/**
 * Rank all players in a match by MVP Points with deterministic tie-breaking.
 */
export function rankMatchPlayers(match) {
  if (!match || !Array.isArray(match.innings)) return [];

  const playerMap = new Map();

  match.innings.forEach(inn => {
    // Batting stats
    if (Array.isArray(inn.batsmen)) {
      inn.batsmen.forEach(b => {
        if (!b || !b.name) return;
        const cleanName = String(b.name).trim().replace(/\s+/g, ' ');
        const key = cleanName.toLowerCase();
        const existing = playerMap.get(key) || { name: cleanName, runs: 0, fours: 0, sixes: 0, wickets: 0, maidens: 0, catches: 0, runOuts: 0 };
        existing.runs += toNumber(b.runs);
        existing.fours += toNumber(b.fours);
        existing.sixes += toNumber(b.sixes);
        playerMap.set(key, existing);
      });
    }

    // Bowling stats
    if (Array.isArray(inn.bowlers)) {
      inn.bowlers.forEach(bw => {
        if (!bw || !bw.name) return;
        const cleanName = String(bw.name).trim().replace(/\s+/g, ' ');
        const key = cleanName.toLowerCase();
        const existing = playerMap.get(key) || { name: cleanName, runs: 0, fours: 0, sixes: 0, wickets: 0, maidens: 0, catches: 0, runOuts: 0 };
        existing.wickets += toNumber(bw.wickets);
        existing.maidens += toNumber(bw.maidens);
        playerMap.set(key, existing);
      });
    }
    // Fielding stats
    if (Array.isArray(inn.ballLog)) {
      inn.ballLog.forEach(ball => {
        if (ball.fielder) {
          const cleanName = String(ball.fielder).trim().replace(/\s+/g, ' ');
          const key = cleanName.toLowerCase();
          const existing = playerMap.get(key) || { name: cleanName, runs: 0, fours: 0, sixes: 0, wickets: 0, maidens: 0, catches: 0, runOuts: 0 };
          if (ball.wicketType === 'Caught') existing.catches += 1;
          if (ball.wicketType === 'RunOut') existing.runOuts += 1;
          playerMap.set(key, existing);
        }
      });
    }
  });

  const ranked = Array.from(playerMap.values()).map(p => {
    const pts = calculatePlayerMvpPoints(p);
    const tier = getMvpRankTier(pts);
    return { ...p, mvpPoints: pts, tier };
  });

  // Deterministic sorting: MVP Points -> Wickets -> Runs -> Name
  return ranked.sort((a, b) => {
    if (b.mvpPoints !== a.mvpPoints) return b.mvpPoints - a.mvpPoints;
    if (b.wickets !== a.wickets) return b.wickets - a.wickets;
    if (b.runs !== a.runs) return b.runs - a.runs;
    return a.name.localeCompare(b.name);
  });
}

/**
 * Calculate single match MVP winner
 */
export function calculateMatchMVP(match) {
  const ranked = rankMatchPlayers(match);
  return ranked.length > 0 ? ranked[0] : null;
}

/**
 * Aggregate stats across all tournament matches to calculate Tournament MVP & Award Winners
 */
export function aggregateTournamentStats(matches = []) {
  const playerStats = new Map();

  matches.forEach(m => {
    if (!m || !Array.isArray(m.innings)) return;
    m.innings.forEach(inn => {
      // Batting
      if (Array.isArray(inn.batsmen)) {
        inn.batsmen.forEach(b => {
          if (!b || !b.name) return;
          const name = String(b.name).trim();
          const key = name.toLowerCase();
          const p = playerStats.get(key) || { name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, bowlerBalls: 0, bowlerRuns: 0, maidens: 0, catches: 0, runOuts: 0 };
          p.runs += toNumber(b.runs);
          p.balls += toNumber(b.balls);
          p.fours += toNumber(b.fours);
          p.sixes += toNumber(b.sixes);
          playerStats.set(key, p);
        });
      }
      // Bowling
      if (Array.isArray(inn.bowlers)) {
        inn.bowlers.forEach(bw => {
          if (!bw || !bw.name) return;
          const name = String(bw.name).trim();
          const key = name.toLowerCase();
          const p = playerStats.get(key) || { name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, bowlerBalls: 0, bowlerRuns: 0, maidens: 0, catches: 0, runOuts: 0 };
          p.wickets += toNumber(bw.wickets);
          p.bowlerBalls += toNumber(bw.balls);
          p.bowlerRuns += toNumber(bw.runs);
          p.maidens += toNumber(bw.maidens);
          playerStats.set(key, p);
        });
      }
      // Ball log for fielding (catches & runouts)
      if (Array.isArray(inn.ballLog)) {
        inn.ballLog.forEach(ball => {
          if (ball.fielder) {
            const name = String(ball.fielder).trim();
            const key = name.toLowerCase();
            const p = playerStats.get(key) || { name, runs: 0, balls: 0, fours: 0, sixes: 0, wickets: 0, bowlerBalls: 0, bowlerRuns: 0, maidens: 0, catches: 0, runOuts: 0 };
            if (ball.wicketType === 'Caught') p.catches += 1;
            if (ball.wicketType === 'RunOut') p.runOuts += 1;
            playerStats.set(key, p);
          }
        });
      }
    });
  });

  const list = Array.from(playerStats.values()).map(p => {
    const pts = calculatePlayerMvpPoints(p);
    const econ = p.bowlerBalls > 0 ? ((p.bowlerRuns / p.bowlerBalls) * 6).toFixed(2) : "Infinity";
    const sr = p.balls > 0 ? ((p.runs / p.balls) * 100).toFixed(1) : "0.0";
    return { ...p, points: pts, econ, sr, tier: getMvpRankTier(pts) };
  });

  const sortedByMvp = [...list].sort((a, b) => b.points - a.points);
  const sortedByRuns = [...list].sort((a, b) => b.runs - a.runs);
  const sortedByWkts = [...list].sort((a, b) => b.wickets - a.wickets || parseFloat(a.econ) - parseFloat(b.econ));
  const sortedByFielding = [...list].sort((a, b) => (b.catches + b.runOuts) - (a.catches + a.runOuts));
  const sortedBySixes = [...list].sort((a, b) => b.sixes - a.sixes);

  return {
    mvp: sortedByMvp[0] || null,
    bestBatter: sortedByRuns[0] || null,
    bestBowler: sortedByWkts[0] || null,
    bestFielder: sortedByFielding[0] || null,
    mostSixes: sortedBySixes[0] || null,
    allPlayers: sortedByMvp
  };
}
